import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { entities, getDisplayMessage } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Scale,
  Loader2,
  Search,
} from 'lucide-react';
import SelecionarPregaoModal from '@/components/propostas/SelecionarPregaoModal';
import ManifestoTypeSelector from '@/components/manifesto/ManifestoTypeSelector';
import ManifestoImpugnacaoFlow from '@/components/manifesto/ManifestoImpugnacaoFlow';
import ManifestoRecursoFlow from '@/components/manifesto/ManifestoRecursoFlow';
import ManifestoContrarrazaoFlow from '@/components/manifesto/ManifestoContrarrazaoFlow';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TIPO_LABEL = {
  impugnacao: 'Impugnação',
  recurso_administrativo: 'Recurso administrativo',
  contrarrazao: 'Contrarrazão',
  // Legado: mantém visualização de registros antigos sem quebrar listagem.
  esclarecimento_pedido: 'Pedido de esclarecimento (legado)',
  diligencia: 'Diligência (legado)',
  intencao_recurso: 'Intenção de recurso (legado)',
};

const TIPO_CREATE_OPTIONS = [
  { value: 'impugnacao', label: 'Impugnação' },
  { value: 'recurso_administrativo', label: 'Recurso administrativo' },
  { value: 'contrarrazao', label: 'Contrarrazão' },
];

const STATUS_LABEL = {
  rascunho: 'Rascunho',
  em_revisao: 'Em revisão',
  aprovado: 'Aprovado',
  protocolado: 'Protocolado',
  arquivado: 'Arquivado',
};

function formatDt(v) {
  if (v == null) return '—';
  try {
    const d = typeof v === 'string' ? parseISO(v) : new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return '—';
  }
}

export default function Manifesto() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editalId, setEditalId] = useState(() => searchParams.get('editalId') || '');
  const [selectedId, setSelectedId] = useState(null);
  const [activeTipo, setActiveTipo] = useState('impugnacao');
  const [modalEditalAberto, setModalEditalAberto] = useState(false);

  useEffect(() => {
    const q = searchParams.get('editalId');
    if (q && q !== editalId) setEditalId(q);
  }, [searchParams, editalId]);

  /** Mesma base de editais que «Nova Lição Aprendida» + SelecionarPregaoModal (aprovar seguir). */
  const editaisQuery = useQuery({
    queryKey: ['editais-manifesto-pregao'],
    queryFn: async () => {
      const data = await entities.Edital.list('-createdAt', 200);
      return (Array.isArray(data) ? data : []).filter(
        (e) => (e.status_aprovacao || e.statusAprovacao) === 'aprovar_seguir'
      );
    },
  });

  const manifestListQuery = useQuery({
    queryKey: ['manifesto', 'list', editalId],
    queryFn: () => entities.Manifesto.listManifestacoes(editalId),
    enabled: !!editalId,
  });

  const detailQuery = useQuery({
    queryKey: ['manifesto', 'detail', editalId, selectedId],
    queryFn: () => entities.Manifesto.getManifestacao(editalId, selectedId),
    enabled: !!editalId && !!selectedId,
  });

  const current = detailQuery.data;

  useEffect(() => {
    if (current?.tipo && current.tipo !== activeTipo && TIPO_CREATE_OPTIONS.some((o) => o.value === current.tipo)) {
      setActiveTipo(current.tipo);
    }
  }, [current?.tipo, activeTipo]);

  const manifestacoesFiltradas = useMemo(
    () => (manifestListQuery.data || []).filter((m) => m.tipo === activeTipo),
    [manifestListQuery.data, activeTipo]
  );

  /** Linha da tabela ou detalhe: permite abrir o fluxo antes do GET /manifestacoes/:id concluir. */
  const manifestacaoSelecionadaResumo = useMemo(
    () => (manifestListQuery.data || []).find((m) => m.id === selectedId),
    [manifestListQuery.data, selectedId]
  );
  const tipoManifestacaoAberta = current?.tipo ?? manifestacaoSelecionadaResumo?.tipo;
  const statusManifestacaoAberta = current?.status ?? manifestacaoSelecionadaResumo?.status;
  const tituloManifestacaoAberta = current?.titulo ?? manifestacaoSelecionadaResumo?.titulo;

  const recursosNoEdital = useMemo(() => {
    const rows = (manifestListQuery.data || []).filter((m) => m.tipo === 'recurso_administrativo');
    return [...rows].sort((a, b) => {
      const ta = new Date(a.updatedAt ?? 0).getTime();
      const tb = new Date(b.updatedAt ?? 0).getTime();
      return tb - ta;
    });
  }, [manifestListQuery.data]);

  const createMut = useMutation({
    mutationFn: (body) => entities.Manifesto.createManifestacao(editalId, body),
    onSuccess: (data) => {
      toast.success('Manifestação criada.');
      queryClient.invalidateQueries({ queryKey: ['manifesto', 'list', editalId] });
      if (data?.id) setSelectedId(data.id);
    },
    onError: (e) => {
      const msg = getDisplayMessage(e);
      if (e?.data?.code === 'MODULE_FORBIDDEN' || msg.includes('Módulo não habilitado')) {
        toast.error('O módulo Manifesto não está habilitado no seu plano ou permissões.');
      } else toast.error(msg || 'Erro ao criar.');
    },
  });

  const onEditalChange = (v) => {
    setEditalId(v);
    setSelectedId(null);
    const next = new URLSearchParams(searchParams);
    if (v) next.set('editalId', v);
    else next.delete('editalId');
    setSearchParams(next, { replace: true });
  };

  const editaisParaModal = editaisQuery.data || [];

  const textoEditalSelecionado = useMemo(() => {
    if (!editalId) return '';
    const edital = editaisParaModal.find((e) => e.id === editalId);
    if (!edital) return `Edital selecionado (ID: ${editalId.slice(0, 8)}…)`;
    const inst = edital.instituicao || edital.titulo_licitacao || edital.nome_arquivo || edital.nomeArquivo || '';
    const pe = edital.numero_pregao || edital.numeroPregao ? ` • ${edital.numero_pregao || edital.numeroPregao}` : '';
    const proc = edital.numero_processo || edital.numeroProcesso ? ` • ${edital.numero_processo || edital.numeroProcesso}` : '';
    return inst ? `${inst}${pe}${proc}` : `Edital (ID: ${edital.id.slice(0, 8)}…)`;
  }, [editalId, editaisParaModal]);

  function handleTipoSelect(tipo) {
    if (!editalId) {
      toast.error('Selecione primeiro o processo licitatório.');
      return;
    }
    if (createMut.isPending) return;
    setActiveTipo(tipo);
    setSelectedId(null);

    if (tipo === 'contrarrazao') {
      if (recursosNoEdital.length === 0) {
        toast.error('Crie primeiro um recurso administrativo para vincular a contrarrazão.');
        return;
      }
      createMut.mutate({
        tipo,
        manifestacaoPaiId: recursosNoEdital[0].id,
      });
      return;
    }

    createMut.mutate({ tipo });
  }

  const salvarFinalMut = useMutation({
    mutationFn: async ({ minutaConteudo, observacoesPayload, statusAtual: stIn }) => {
      if (!editalId || !selectedId) {
        throw new Error('Selecione o edital e uma manifestação.');
      }
      const statusAtual = stIn;
      if (statusAtual === 'protocolado' || statusAtual === 'arquivado') {
        throw new Error('Manifestação protocolada ou arquivada não pode ser alterada.');
      }
      const obs = JSON.stringify(observacoesPayload);
      if (obs.length > 900_000) {
        throw new Error('Os dados da análise são grandes demais para gravar. Reduza anexos ou refaça a análise.');
      }
      const textoMinuta = typeof minutaConteudo === 'string' ? minutaConteudo.trim() : '';
      if (textoMinuta) {
        await entities.Manifesto.postPecaVersao(editalId, selectedId, {
          conteudo: textoMinuta,
          origem: 'usuario',
        });
      }
      const patchBody = { observacoes: obs };
      if (statusAtual === 'rascunho') patchBody.status = 'em_revisao';
      else if (statusAtual === 'em_revisao') patchBody.status = 'aprovado';
      await entities.Manifesto.patchManifestacao(editalId, selectedId, patchBody);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['manifesto', 'list', editalId] });
      queryClient.invalidateQueries({ queryKey: ['manifesto', 'detail', editalId, selectedId] });
      const st = variables?.statusAtual;
      if (st === 'rascunho') {
        toast.success('Dados salvos. Status: em revisão.');
      } else if (st === 'em_revisao') {
        toast.success('Dados salvos. Manifestação aprovada para protocolo.');
      } else {
        toast.success('Dados salvos.');
      }
    },
    onError: (e) => {
      toast.error(getDisplayMessage(e, 'Não foi possível salvar.'));
    },
  });

  return (
    <div className="container max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-tour="tour-Manifesto-root">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Scale className="h-7 w-7 text-blue-600" />
            Manifesto
          </h1>
          <p className="text-sm text-slate-600 mt-1">Impugnações, recursos administrativos e contrarrazões vinculadas ao edital.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Edital</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-base font-medium">Selecione o processo licitatório</Label>
            <p className="text-sm text-slate-600 mt-1">
              Busque por instituição, número do pregão, processo ou objeto e clique em &quot;Consultar pregão&quot; para
              selecionar (mesmo fluxo de Nova Lição Aprendida).
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <div className="flex-1 rounded-lg border border-input bg-muted/40 px-3 py-2 text-sm text-slate-700 min-h-[44px] flex items-center">
              {editalId ? textoEditalSelecionado : 'Nenhum edital selecionado — clique em Consultar pregão para buscar'}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setModalEditalAberto(true)}
              className="shrink-0 gap-2 h-11 w-full sm:w-auto"
              disabled={editaisQuery.isLoading}
              data-tour="tour-Manifesto-primary"
            >
              {editaisQuery.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Consultar pregão
            </Button>
          </div>
          <SelecionarPregaoModal
            open={modalEditalAberto}
            onOpenChange={setModalEditalAberto}
            editais={editaisParaModal}
            editalSelecionadoId={editalId || null}
            onSelecionar={(edital) => {
              if (edital?.id) onEditalChange(edital.id);
            }}
            statusFiltro="todos"
            textoAjuda='Busque por instituição, número do pregão, processo ou objeto e clique em "Selecionar" para vincular ao Manifesto.'
          />
          {editaisQuery.isError ? (
            <p className="text-sm text-red-600">Não foi possível carregar editais.</p>
          ) : null}
        </CardContent>
      </Card>

      {editalId ? (
        <ManifestoTypeSelector
          value={activeTipo}
          onChange={handleTipoSelect}
          disabled={createMut.isPending}
        />
      ) : null}

      {editalId ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Manifestações — {TIPO_LABEL[activeTipo]}</CardTitle>
          </CardHeader>
          <CardContent>
            {manifestListQuery.isLoading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" /> Carregando…
              </div>
            ) : manifestListQuery.isError ? (
              <p className="text-sm text-red-600">
                {String(manifestListQuery.error?.message || '').includes('403') ||
                String(getDisplayMessage(manifestListQuery.error)).includes('Módulo')
                  ? 'O módulo Manifesto não está habilitado para sua empresa ou usuário. Solicite ao administrador.'
                  : getDisplayMessage(manifestListQuery.error) || 'Erro ao listar.'}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Versão</TableHead>
                    <TableHead>Atualizado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manifestacoesFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-slate-500 text-center py-8">
                        Nenhuma manifestação de {TIPO_LABEL[activeTipo]} neste edital. Clique no tipo acima para criar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    manifestacoesFiltradas.map((row) => (
                      <TableRow
                        key={row.id}
                        className={selectedId === row.id ? 'bg-blue-50 cursor-pointer' : 'cursor-pointer'}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <TableCell>{TIPO_LABEL[row.tipo] || row.tipo}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{STATUS_LABEL[row.status] || row.status}</Badge>
                        </TableCell>
                        <TableCell>{row.titulo || '—'}</TableCell>
                        <TableCell>{row.ultimaVersaoPeca ?? '—'}</TableCell>
                        <TableCell>{formatDt(row.updatedAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : null}

      {editalId && selectedId && tipoManifestacaoAberta ? (
        <>
          {tipoManifestacaoAberta === 'impugnacao' ? (
            <ManifestoImpugnacaoFlow
              editalId={editalId}
              manifestacaoId={selectedId}
              onSalvarRascunho={() => toast.success('Rascunho salvo.')}
              onSalvarFinal={(payload) =>
                salvarFinalMut.mutateAsync({
                  minutaConteudo: payload.minutaConteudo,
                  statusAtual: statusManifestacaoAberta,
                  observacoesPayload: {
                    ...payload.observacoesPayload,
                    editalRotulo: textoEditalSelecionado || null,
                    manifestacaoTitulo: tituloManifestacaoAberta || null,
                  },
                })
              }
              salvandoFinal={salvarFinalMut.isPending}
              onAvancar={() => {}}
              editalLabel={textoEditalSelecionado}
            />
          ) : null}
          {tipoManifestacaoAberta === 'recurso_administrativo' ? (
            <ManifestoRecursoFlow
              editalId={editalId}
              manifestacaoId={selectedId}
              onSalvarRascunho={() => toast.success('Rascunho salvo.')}
              onSalvarFinal={(payload) =>
                salvarFinalMut.mutateAsync({
                  minutaConteudo: payload.minutaConteudo,
                  statusAtual: statusManifestacaoAberta,
                  observacoesPayload: {
                    ...payload.observacoesPayload,
                    editalRotulo: textoEditalSelecionado || null,
                    manifestacaoTitulo: tituloManifestacaoAberta || null,
                  },
                })
              }
              salvandoFinal={salvarFinalMut.isPending}
              onAvancar={() => {}}
              editalLabel={textoEditalSelecionado}
            />
          ) : null}
          {tipoManifestacaoAberta === 'contrarrazao' ? (
            <ManifestoContrarrazaoFlow
              editalId={editalId}
              manifestacaoId={selectedId}
              onSalvarRascunho={() => toast.success('Rascunho salvo.')}
              onSalvarFinal={(payload) =>
                salvarFinalMut.mutateAsync({
                  minutaConteudo: payload.minutaConteudo,
                  statusAtual: statusManifestacaoAberta,
                  observacoesPayload: {
                    ...payload.observacoesPayload,
                    editalRotulo: textoEditalSelecionado || null,
                    manifestacaoTitulo: tituloManifestacaoAberta || null,
                  },
                })
              }
              salvandoFinal={salvarFinalMut.isPending}
              onAvancar={() => {}}
              editalLabel={textoEditalSelecionado}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
